// @ts-nocheck
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  Box,
  Checkbox,
  Button,
  Icon,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { DataTable, EmptyState } from '../../../../../common/components';
import { DateUtils, StringUtils } from '../../../../../common/utils';
import { useDialog, useOnChange } from '../../../../../common/hooks';
import CampaignForm from '../../common/CampaignForm';

const useStyles = makeStyles(() => ({
  container: {
    flex: '1',
    height: '100%',
    minHeight: '500px',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  dialog: {
    padding: '0',
    marginBottom: '0',
  },
  dialogContent: {
    display: 'flex',
    maxWidth: '1200px',
    overflow: 'hidden',
  },
  checkbox: {
    flex: '1',
    justifyContent: 'flex-start',
    pointerEvents: 'none',
  },
}));

function CampaignList(props) {
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();

  const {
    data,
    products,
    onCreateCampaign,
    onSelectCampaign,
    onPageChange,
    page,
    actionsDisabled,
    filterParams,
    onFilterChange,
    sortParams,
    onSortChange,
  } = props;

  const [sortModel, setSortModel] = useState([
    {
      field: sortParams.orderBy,
      sort: sortParams.sortOrder,
    },
  ]);

  useOnChange(sortModel, () => {
    if (_.isEmpty(sortModel)) {
      return;
    }

    onSortChange({
      orderBy: sortModel[0].field,
      sortOrder: sortModel[0].sort,
    });
  });

  const handleSortModelChange = params => {
    if (!_.isEqual(params.sortModel, sortModel)) {
      setSortModel(params.sortModel);
    }
  };

  const getFlowType = params => {
    const { value: flowType } = params;
    switch (flowType) {
      case 'OPTIN':
        return 'OptIn';
      case 'OPTOUT':
        return 'OptOut';
      case 'UPGRADE':
        return 'FreeUpgrade - OptIn';
      case 'EXTENDED_ACCESS':
        return 'FreeUpgrade - OptIn';
      case 'DISCOUNTED_UPGRADE':
        return 'FreeUpgrade - OptOut';
      case 'B2B':
        return 'B2B';
      case 'DISCOUNT':
        return 'Rabat'
      default:
        return '';
    }
  };

  const getColumnDefs = () => [
    {
      field: 'title',
      headerName: t('campaign.campaignTitle'),
      width: 350,
    },
    {
      field: 'productId',
      headerName: t('campaign.campaignProductId'),
      width: 135,
    },
    {
      field: 'discountDays',
      headerName: t('campaign.campaignDiscountDays'),
      width: 150,
      valueGetter: params => StringUtils.getFormattedDays(params.value),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'type',
      headerName: t('campaign.campaignType'),
      width: 150,
    },
    {
      field: 'flowType',
      headerName: t('campaign.campaignFlowType'),
      width: 150,
      valueGetter: getFlowType,
      sortComparator: StringUtils.compare,
    },
    {
      field: 'autoCancel',
      headerName: t('campaign.campaignAutoCancel'),
      headerAlign: 'left',
      width: 155,
      type: 'boolean',
      renderCell: params => <Checkbox checked={params.value} className={classes.checkbox} />,
    },
    {
      field: 'created',
      headerName: t('campaign.campaignCreated'),
      width: 200,
      type: 'dateTime',
      valueFormatter: params => DateUtils.getDateTime(params.value),
    },
    {
      field: 'discountDuration',
      headerName: t('campaign.campaignDiscountDuration'),
      width: 150,
      valueGetter: params => StringUtils.getFormattedDays(params.value),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'discountType',
      headerName: t('campaign.campaignDiscountType'),
      width: 175,
    },
    {
      field: 'discountValue',
      headerName: t('campaign.campaignDiscountValue'),
      width: 175,
      valueGetter: params => params.row.discountType === 'PERCENT' ? `${params.value} %` : StringUtils.getFormattedSum(params.value),
    },
  ];

  const getRowData = () => data.content;

  const handleShowCreateCampaignDialog = () => {
    showDialog({
      title: t('campaign.campaignCreate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <CampaignForm
            products={products}
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            saveButtonText={t('common.create')}
          />
        </Box>
      ),
      onConfirm: onCreateCampaign,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  const handleFilterChange = event => {
    const filter = {
      ...filterParams,
    };

    if (event.target.id === 'CampaignList-showWithActiveSeries') {
      filter.withActiveSeries = event.target.checked;
    } else if (event.target.id === 'CampaignList-showActive') {
      filter.onlyActive = event.target.checked;
    }

    onFilterChange(filter);
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="CampaignList-showActive"
            color="primary"
            checked={filterParams.onlyActive}
            onChange={handleFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('common.onlyActive')}
      />
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="CampaignList-showWithActiveSeries"
            color="primary"
            checked={filterParams.withActiveSeries}
            onChange={handleFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('campaign.campaignShowWithActiveSeries')}
      />
      <Button
        id="CampaignList-createCampaign"
        variant="outlined"
        color="primary"
        startIcon={<Icon>campaign</Icon>}
        onClick={handleShowCreateCampaignDialog}
        disabled={actionsDisabled}
      >
        {t('campaign.campaignCreate')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('campaign.campaignsEmpty')} />
    </GridOverlay>
  );

  return (
    <Box id="CampaignList-container" className={classes.container}>
      <DataTable
        id="CampaignList-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        rowCount={data.total}
        onPageChange={onPageChange}
        onRowClick={onSelectCampaign}
        pageSize={20}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        sortingMode="server"
        paginationMode="server"
        pageIndex={page - 1}
        pagination
      />
    </Box>
  );
}

CampaignList.propTypes = {
  data: PropTypes.shape({
    size: PropTypes.number,
    items: PropTypes.number,
    total: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number,
    content: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  onCreateCampaign: PropTypes.func.isRequired,
  onSelectCampaign: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
  filterParams: PropTypes.shape({
    onlyActive: PropTypes.bool,
    withActiveSeries: PropTypes.bool,
  }).isRequired,
  sortParams: PropTypes.shape({
    orderBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
};

export default CampaignList;
